import React from 'react';
import { Link } from 'react-router-dom';
import { BsTwitter, BsFacebook, BsLinkedin } from 'react-icons/bs';
import { FaComment, FaClock } from 'react-icons/fa';
import { format } from 'date-fns';
import CommentSection from '../common/CommentSection';
import RelatedPosts from '../common/RelatedPosts';
import AuthorInfo from '../common/AuthorInfo';
import CategoryList from '../common/CategoryList';
import SearchBar from '../common/SearchBar';
import RecentPosts from '../common/RecentPosts';
import PopularPosts from '../common/PopularPosts';
import TagsList from '../common/TagsList';
import StatusBadge from '../../common/StatusBadge';
import BlogInteractions from '../common/BlogInteractions';

const BlogDetail = ({ blog }) => {
  if (!blog) return <div>Loading...</div>;

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = blog.title;
    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="container mx-auto py-8">
      {/* Full Width Feature Image with Overlay Title */}
      <div className="relative w-full h-96 mb-8">
        {blog.featureImage && (
          <img
            src={blog.featureImage}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-center items-center text-white p-4">
          <h1 className="text-5xl font-bold text-center">{blog.title}</h1>
          <div className="flex items-center space-x-4 mt-4">
            <span className="flex items-center">
              <FaClock className="mr-2" />
              {blog.readingTime} min read
            </span>
            <BlogInteractions 
              blogId={blog._id}
              initialCounts={{
                views: blog.metadata?.views || 0,
                likes: blog.metadata?.likes || 0,
                shares: blog.metadata?.shares || 0
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content Column */}
        <div className="col-span-3 space-y-8">
          {/* Row 1 - Social Icons */}
          <div className="flex justify-center space-x-4 mb-4">
            <button onClick={() => handleShare('twitter')} className="text-blue-500 hover:text-blue-600">
              <BsTwitter size={24} />
            </button>
            <button onClick={() => handleShare('facebook')} className="text-blue-800 hover:text-blue-900">
              <BsFacebook size={24} />
            </button>
            <button onClick={() => handleShare('linkedin')} className="text-blue-600 hover:text-blue-700">
              <BsLinkedin size={24} />
            </button>
          </div>

          {/* Row 2 - Title and Meta Info */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <Link to={`/blog?author=${blog.author?._id}`} className="hover:text-gray-700">
                By {blog.authorName}
              </Link>
              <span>•</span>
              <span>{format(new Date(blog.publishedAt || blog.createdAt), 'MMM d, yyyy')}</span>
              <StatusBadge status={blog.status} />
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <FaComment className="mr-2" />
                {blog.commentCount || 0} comments
              </span>
            </div>
          </div>

          {/* Row 3 - Short Description */}
          {blog.shortDescription && (
            <div className="text-lg text-gray-600 dark:text-gray-400 italic">
              {blog.shortDescription}
            </div>
          )}

          {/* Row 4 - Content */}
          <div className="rich-text-content text-gray-700 dark:text-gray-300 leading-relaxed" 
               dangerouslySetInnerHTML={{ __html: blog.content }} />

          {/* Row 5 - Categories and Tags */}
          {(blog.categoryNames?.length > 0 || blog.tagNames?.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {blog.categoryNames?.map((category, index) => (
                <Link
                  key={index}
                  to={`/blog?category=${encodeURIComponent(category)}`}
                  className="px-3 py-1 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {category}
                </Link>
              ))}
              {blog.tagNames?.map((tag, index) => (
                <Link
                  key={index}
                  to={`/blog?tag=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                >
                  {tag}
                </Link>
              ))}
            </div>
          )}

          {/* Row 6 - Author Info */}
          <AuthorInfo author={blog.author} />

          {/* Row 7 - Comments Section */}
          {blog.commentsEnabled && (
            <CommentSection blogId={blog._id} />
          )}
        </div>

        {/* Sidebar Column */}
        <div className="col-span-1 space-y-6">
          <SearchBar />
          <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">About Us</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Welcome to our blog! Here you can find articles about various topics written by our expert authors.
            </p>
          </div>
          <CategoryList />
          <RecentPosts />
          <PopularPosts />
          <TagsList />
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
